import { useState, useEffect, useRef } from 'react'
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap'
import client from '../api/client'
import ChatMessage from '../components/ChatMessage'
import ChatInput from '../components/ChatInput'

const STORAGE_KEY = 'admin_test_chat_messages'
const MODEL_KEY = 'admin_test_chat_model'

function TestChat() {
  const [models, setModels] = useState([])
  const [selectedModel, setSelectedModel] = useState('')
  const [messages, setMessages] = useState([])
  const [loading, setLoading] = useState(false)
  const [modelsLoading, setModelsLoading] = useState(true)
  const [error, setError] = useState('')
  const messagesEndRef = useRef(null)
  const abortControllerRef = useRef(null)

  useEffect(() => {
    fetchModels()
  }, [])

  useEffect(() => {
    const savedMessages = sessionStorage.getItem(STORAGE_KEY)
    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages))
      } catch (e) {
        console.error('Failed to parse saved messages:', e)
      }
    }
    const savedModel = sessionStorage.getItem(MODEL_KEY)
    if (savedModel) {
      setSelectedModel(savedModel)
    }
  }, [])

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
    }
  }, [messages])

  useEffect(() => {
    if (selectedModel) {
      sessionStorage.setItem(MODEL_KEY, selectedModel)
    }
  }, [selectedModel])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const fetchModels = async () => {
    setModelsLoading(true)
    try {
      const response = await client.get('/admin/api/v1/models/', {
        params: { limit: 500 }
      })
      const allModels = response.data.models || []
      setModels(allModels)
      if (!selectedModel && allModels.length > 0) {
        setSelectedModel(allModels[0].code)
      }
    } catch (err) {
      setError('Erreur lors du chargement des modèles')
      console.error(err)
    } finally {
      setModelsLoading(false)
    }
  }

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  const handleSend = async ({ text, image }) => {
    if (!selectedModel) {
      setError('Veuillez sélectionner un modèle')
      return
    }

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: text,
      image: image,
      timestamp: new Date().toISOString(),
    }

    const assistantMessageId = Date.now() + 1
    setMessages((prev) => [
      ...prev,
      userMessage,
      { id: assistantMessageId, role: 'assistant', content: '', timestamp: new Date().toISOString() },
    ])
    setLoading(true)
    setError('')

    const apiMessages = messages
      .filter(m => m.role === 'user' || m.role === 'assistant')
      .map(m => ({ role: m.role, content: m.content }))

    apiMessages.push({
      role: 'user',
      content: image ? [
        { type: 'text', text: text || 'Décrivez cette image' },
        { type: 'image_url', image_url: { url: image } }
      ] : text,
    })

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const baseUrl = import.meta.env.VITE_API_URL || ''
      const token = localStorage.getItem('adminToken')

      const response = await fetch(`${baseUrl}/api/v1/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          model: selectedModel,
          messages: apiMessages,
          temperature: 0.7,
          max_tokens: 2000,
          stream: true,
        }),
        signal: controller.signal,
      })

      if (response.status === 401) {
        localStorage.removeItem('adminToken')
        window.location.href = '/login'
        return
      }
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || `Erreur HTTP ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let fullContent = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed || !trimmed.startsWith('data: ')) continue
          const data = trimmed.slice(6)
          if (data === '[DONE]') continue
          try {
            const parsed = JSON.parse(data)
            const delta = parsed.choices?.[0]?.delta?.content
            if (delta) {
              fullContent += delta
              const snapshot = fullContent
              setMessages((prev) =>
                prev.map((m) => m.id === assistantMessageId ? { ...m, content: snapshot } : m)
              )
            }
          } catch {
          }
        }
      }

      if (!fullContent) {
        setMessages((prev) =>
          prev.map((m) => m.id === assistantMessageId ? { ...m, content: 'Pas de réponse' } : m)
        )
      }
    } catch (err) {
      if (err.name === 'AbortError') return
      console.error('Chat error:', err)
      const errorMessage = err.message || "Erreur lors de l'envoi du message"
      setError(errorMessage)
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantMessageId ? { ...m, content: `⚠️ Erreur: ${errorMessage}` } : m
        )
      )
    } finally {
      abortControllerRef.current = null
      setLoading(false)
    }
  }

  const clearConversation = () => {
    setMessages([])
    sessionStorage.removeItem(STORAGE_KEY)
  }

  const getModelInfo = (code) => {
    return models.find(m => m.code === code)
  }

  const selectedModelInfo = getModelInfo(selectedModel)

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <h1 className="h3">Test de modèle</h1>
          <p className="text-muted">
            Testez n'importe quel modèle LLM avec support multimodal (texte et images)
          </p>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Row>
        {/* Model selector sidebar */}
        <Col md={3}>
          <Card className="mb-3">
            <Card.Header>Sélection du modèle</Card.Header>
            <Card.Body>
              {modelsLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : (
                <>
                  <Form.Select
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="mb-3"
                  >
                    <option value="">Sélectionner un modèle...</option>
                    {models.map((model) => (
                      <option key={model.id} value={model.code}>
                        {model.name || model.code}
                        {!model.active && ' (inactif)'}
                      </option>
                    ))}
                  </Form.Select>

                  {selectedModelInfo && (
                    <div className="small text-muted">
                      <strong>Fournisseur:</strong> {selectedModelInfo.provider}
                      <br />
                      <strong>Type:</strong> {selectedModelInfo.type || 'LLM'}
                      <br />
                      {selectedModelInfo.pricing_input && (
                        <>
                          <strong>Coût entrée:</strong> ${selectedModelInfo.pricing_input}/1K
                          <br />
                        </>
                      )}
                      {selectedModelInfo.pricing_output && (
                        <>
                          <strong>Coût sortie:</strong> ${selectedModelInfo.pricing_output}/1K
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>Actions</Card.Header>
            <Card.Body>
              <Button
                variant="outline-danger"
                className="w-100"
                onClick={clearConversation}
                disabled={messages.length === 0}
              >
                Effacer la conversation
              </Button>
              <div className="text-muted small mt-2">
                {messages.length} message{messages.length !== 1 ? 's' : ''} dans la session
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Chat area */}
        <Col md={9}>
          <Card className="h-100" style={{ minHeight: '500px' }}>
            <Card.Header className="bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <span>Conversation</span>
                {selectedModelInfo && (
                  <span className={`badge ${selectedModelInfo.active ? 'bg-success' : 'bg-secondary'}`}>
                    {selectedModelInfo.name || selectedModelInfo.code}
                  </span>
                )}
              </div>
            </Card.Header>
            <Card.Body
              className="overflow-auto"
              style={{ maxHeight: '400px', minHeight: '300px' }}
            >
              {messages.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <p>Aucun message</p>
                  <p className="small">
                    Sélectionnez un modèle et commencez à taper pour tester
                  </p>
                </div>
              ) : (
                messages.map((message) => (
                  <ChatMessage key={message.id} message={message} />
                ))
              )}
              <div ref={messagesEndRef} />
            </Card.Body>
            <Card.Footer className="bg-white">
              {loading ? (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" className="me-2" />
                  <Button variant="outline-danger" size="sm" onClick={handleStop}>
                    Arrêter la génération
                  </Button>
                </div>
              ) : (
                <ChatInput onSend={handleSend} disabled={!selectedModel} />
              )}
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default TestChat
